<template>
  <div
    class="assignment-block"
    :class="{ 'assignment-block--expanded': expanded }"
    :data-testid="`setup-tasklist__assignment-block--${index}`"
  >
    <button
      type="button"
      class="assignment-block__header"
      :data-testid="`setup-tasklist__assignment-block-toggle--${index}`"
      @click="$emit('toggle')"
    >
      <span class="assignment-block__title" data-testid="setup-tasklist__assignment-block-title">
        Assignment {{ assignmentNumber }}*
      </span>
      <span class="assignment-block__header-actions">
        <v-btn
          v-if="removable"
          icon
          x-small
          class="assignment-block__remove-btn"
          data-testid="setup-tasklist__assignment-block-remove"
          @click.stop="$emit('remove')"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
        <v-icon
          size="22"
          class="assignment-block__chevron"
          :class="{ 'assignment-block__chevron--expanded': expanded }"
        >
          mdi-chevron-down
        </v-icon>
      </span>
    </button>

    <div v-show="expanded" class="assignment-block__body">
      <div class="assignment-block__section">
        <p class="assignment-block__field-label assignment-block__field-label--required">
          {{ locationHeader }}
        </p>
        <SearchableMultiSelectDropdown
          :value="localValue.location_ids"
          :items="locationOptions"
          variant="field"
          :label="locationHeader"
          :placeholder="`Select ${locationHeader}`"
          item-text="name"
          item-value="_id"
          max-activator-width="100%"
          data-testid-prefix="setup-tasklist__assignment-locations"
          @input="updateField('location_ids', $event)"
        />
        <p v-if="locationFieldError" class="error--text text-caption mb-0 mt-1">
          {{ locationFieldError }}
        </p>
      </div>

      <div class="assignment-block__section">
        <UserChipMultiSelect
          :value="localValue.preparer"
          variant="add-task"
          label="Preparer(s)"
          :required="true"
          :users="locationScopedUsers"
          :exclude-ids="preparerExcludeIds"
          :loading="usersFetchLoading"
          role-label="preparer"
          placeholder="Select"
          data-testid="setup-tasklist__assignment-preparer"
          :attach="false"
          @input="updateAssignmentRole('preparer', $event)"
        />
        <p v-if="errors.preparer" class="error--text text-caption mb-0 mt-1">
          {{ errors.preparer }}
        </p>
      </div>

      <div class="assignment-block__section">
        <UserChipMultiSelect
          :value="localValue.approver"
          variant="add-task"
          label="Approver(s)"
          :required="true"
          :users="locationScopedUsers"
          :exclude-ids="approverExcludeIds"
          :loading="usersFetchLoading"
          role-label="approver"
          placeholder="Select"
          data-testid="setup-tasklist__assignment-approver"
          :attach="false"
          @input="updateAssignmentRole('approver', $event)"
        />
        <p v-if="errors.approver" class="error--text text-caption mb-0 mt-1">
          {{ errors.approver }}
        </p>
      </div>

      <div class="assignment-block__toggles">
        <div class="assignment-block__section assignment-block__sign-off-section">
          <div class="assignment-block__sign-off" data-testid="setup-tasklist__assignment-sign-off-toggle-row">
            <div class="assignment-block__sign-off-copy">
              <p class="assignment-block__sign-off-title">{{ signOffLabels.REQUIRES_LABEL }}</p>
              <p class="assignment-block__sign-off-desc">{{ signOffLabels.REQUIRES_DESC_SETUP }}</p>
            </div>
            <button
              type="button"
              class="assignment-block__sign-off-toggle"
              :class="{ 'assignment-block__sign-off-toggle--on': localValue.requires_sign_off }"
              :aria-checked="localValue.requires_sign_off ? 'true' : 'false'"
              :aria-label="signOffLabels.REQUIRES_LABEL"
              data-testid="setup-tasklist__assignment-sign-off-toggle"
              @click="onRequiresSignOffToggle"
            >
              <span class="assignment-block__sign-off-toggle-thumb" />
            </button>
          </div>
          <template v-if="localValue.requires_sign_off">
            <UserChipMultiSelect
              :value="localValue.sign_off_approvers"
              variant="add-task"
              :label="signOffLabels.SELECT_APPROVERS_LABEL"
              :users="signOffApproverUsers"
              :exclude-ids="signOffExcludeIds"
              role-label="sign-off approver"
              placeholder="Select"
              data-testid="setup-tasklist__assignment-sign-off-approver"
              :attach="false"
              @input="updateAssignmentRole('sign_off_approvers', $event)"
            />
            <p v-if="errors.sign_off_approvers" class="error--text text-caption mb-0 mt-1">
              {{ errors.sign_off_approvers }}
            </p>
          </template>
          <v-divider
            class="assignment-block__sign-off-divider"
            data-testid="setup-tasklist__assignment-sign-off-divider"
          />
        </div>

        <div v-if="showThreshold" class="assignment-block__section assignment-block__sign-off-section">
          <div class="assignment-block__sign-off" data-testid="setup-tasklist__assignment-threshold-toggle-row">
            <div class="assignment-block__sign-off-copy">
              <p class="assignment-block__sign-off-title">Enforce Threshold</p>
              <p class="assignment-block__sign-off-desc">
                {{ locationHeader }} specific threshold applies and prevents approval until all differences above the
                limit are explained.
              </p>
            </div>
            <button
              type="button"
              class="assignment-block__sign-off-toggle"
              :class="{ 'assignment-block__sign-off-toggle--on': isEnforceThreshold }"
              :aria-checked="isEnforceThreshold ? 'true' : 'false'"
              aria-label="Enforce Threshold"
              data-testid="setup-tasklist__assignment-threshold"
              @click="onSkipValidationsToggle"
            >
              <span class="assignment-block__sign-off-toggle-thumb" />
            </button>
          </div>
          <v-divider
            class="assignment-block__sign-off-divider"
            data-testid="setup-tasklist__assignment-threshold-divider"
          />
        </div>

        <div class="assignment-block__section assignment-block__sign-off-section">
          <div class="assignment-block__sign-off" data-testid="setup-tasklist__assignment-auto-approve-toggle-row">
            <div class="assignment-block__sign-off-copy">
              <p class="assignment-block__sign-off-title">{{ autoApproveLabels.LABEL }}</p>
              <p class="assignment-block__sign-off-desc">{{ autoApproveDescription }}</p>
            </div>
            <button
              type="button"
              class="assignment-block__sign-off-toggle"
              :class="{ 'assignment-block__sign-off-toggle--on': localValue.auto_approve }"
              :aria-checked="localValue.auto_approve ? 'true' : 'false'"
              :aria-label="autoApproveLabels.LABEL"
              data-testid="setup-tasklist__assignment-auto-approve"
              @click="onAutoApproveToggle"
            >
              <span class="assignment-block__sign-off-toggle-thumb" />
            </button>
          </div>
          <v-divider
            class="assignment-block__sign-off-divider"
            data-testid="setup-tasklist__assignment-auto-approve-divider"
          />
        </div>

        <div
          v-if="showAssignAccounts"
          class="assignment-block__section assignment-block__section--accounts"
        >
          <AccountsTab
            variant="assignment"
            :gl-codes="localValue.glcodes"
            :chart-of-accounts="availableAccounts"
            :has-selected-locations="(localValue.location_ids || []).length > 0"
            :consolidated-location-selected="hasConsolidatedLocation"
            :locations="locationOptions"
            :errors="accountsErrors"
            @update:glCodes="updateField('glcodes', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './AssignmentBlock.scss';
import {
  ASSIGNMENT_LOCATION_TYPE,
  AUTO_APPROVE,
  SIGN_OFF,
  USER_ROLE,
} from '../../../../../utils/constants';
import {
  syncAssignmentRolesOnChange,
  normalizeUserIdList,
  excludeIdsForAssignmentRole,
} from '../../utils/assignmentRoles';
import {
  buildAssignmentUserOptions,
  filterActiveAssignmentCatalog,
} from '../../../../Project/TaskList/drawers/AddTaskDialog/addTaskDialogUtils';
import {
  SearchableMultiSelectDropdown,
  UserChipMultiSelect,
} from '../../../../../components';
import AccountsTab from './AccountsTab.vue';
import { filterAccountsForAssignment } from '../../utils/glLocationValidation';
import {
  assignmentHasMixedLocationTypes,
  isConsolidatedOnlyLocationSelection,
} from '@/modules/financialclose/utils/locationMasterType';
import { resolveEnforceThreshold } from '@/modules/financialclose/utils/enforceThresholdHelpers';
import {
  fetchUsersForLocations,
  filterUsersToAllowedIds,
  dropIdsNotAllowed,
} from '../../../../../utils/locationUserAccess';
import { displayLocationLabel } from '../../../../../utils/locationName';

function entityId(entity) {
  if (entity == null) return '';
  if (typeof entity !== 'object') return String(entity);
  const id = entity._id ?? entity.id ?? entity.location_id;
  return id != null && id !== '' ? String(id) : '';
}

export default {
  name: 'AssignmentBlock',

  components: {
    SearchableMultiSelectDropdown,
    UserChipMultiSelect,
    AccountsTab,
  },

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    index: {
      type: Number,
      default: 0,
    },
    assignmentNumber: {
      type: Number,
      default: 1,
    },
    /** When false (Assignment tab hidden), skip usersforlocations until tab opens. */
    active: {
      type: Boolean,
      default: false,
    },
    locations: {
      type: Array,
      default: () => [],
    },
    users: {
      type: Array,
      default: () => [],
    },
    allUsers: {
      type: Array,
      default: () => [],
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    chartOfAccounts: {
      type: Array,
      default: () => [],
    },
    /** GLs selected on Accounts step — assignment picker is limited to this pool. */
    availableGlCodes: {
      type: Array,
      default: () => [],
    },
    taskType: {
      type: String,
      default: '',
    },
    showThreshold: {
      type: Boolean,
      default: false,
    },
    showAssignAccounts: {
      type: Boolean,
      default: false,
    },
    removable: {
      type: Boolean,
      default: true,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      allowedUserIds: [],
      usersFetchLoading: false,
      usersFetchToken: 0,
      /** Location key last successfully resolved; null means never fetched this session. */
      lastFetchedLocationIdsKey: null,
    };
  },

  computed: {
    signOffLabels() {
      return SIGN_OFF;
    },
    autoApproveLabels() {
      return AUTO_APPROVE;
    },
    autoApproveDescription() {
      return this.showThreshold ? AUTO_APPROVE.DESC_AR : AUTO_APPROVE.DESC_CHECKLIST;
    },
    locationHeader() {
      return this.$store.state.LocationHeader || 'Location';
    },
    isEnforceThreshold() {
      return this.localValue.enforceThreshold !== false;
    },
    localValue() {
      const v = this.value || {};
      return {
        assignment_number: this.assignmentNumber,
        location_ids: [...(v.location_ids || [])].map(String),
        preparer: [...(v.preparer || [])].map(String),
        approver: [...(v.approver || [])].map(String),
        requires_sign_off: !!v.requires_sign_off,
        sign_off_approvers: [...(v.sign_off_approvers || [])].map(String),
        enforceThreshold: resolveEnforceThreshold(v, this.taskType),
        auto_approve: !!v.auto_approve,
        glcodes: [...(v.glcodes || v.glcode || [])].map(String),
      };
    },
    accountsErrors() {
      return this.errors.glcode ? { glcode: this.errors.glcode } : {};
    },
    locationOptions() {
      return (this.locations || [])
        .map((l) => {
          const id = entityId(l);
          if (!id) return null;
          return {
            ...l,
            _id: id,
            name: displayLocationLabel(l.name || l.description || ''),
          };
        })
        .filter(Boolean);
    },
    /**
     * True when every selected location is consolidated, so Assign Accounts
     * lists Accounts-step GLs from any entity location.
     */
    hasConsolidatedLocation() {
      return isConsolidatedOnlyLocationSelection(
        this.localValue.location_ids,
        this.locationOptions
      );
    },
    hasMixedLocationTypes() {
      return assignmentHasMixedLocationTypes(
        this.localValue.location_ids,
        this.locationOptions
      );
    },
    locationFieldError() {
      if (this.hasMixedLocationTypes) return ASSIGNMENT_LOCATION_TYPE.MIXED_ERROR;
      return this.errors.location || '';
    },
    /**
     * Assign Accounts: entity locations keep COA mapping. A consolidated
     * location lists every Accounts-step GL regardless of COA location.
     */
    availableAccounts() {
      if (!this.showAssignAccounts) return this.chartOfAccounts || [];
      return filterAccountsForAssignment({
        chartOfAccounts: this.chartOfAccounts,
        availableGlCodes: this.availableGlCodes,
        locationIds: this.localValue.location_ids,
        locations: this.locationOptions,
      });
    },
    activeCatalogUsers() {
      const activeIdSet = new Set();
      const inactiveIdSet = new Set();
      (this.$store.getters['tasks/getActiveUsersIds'] || []).forEach((id) => {
        if (id) activeIdSet.add(String(id));
      });
      (this.$store.getters['tasks/getInactiveUsersList'] || []).forEach((user) => {
        const id = entityId(user);
        if (id) inactiveIdSet.add(id);
      });
      return filterActiveAssignmentCatalog({
        users: this.users,
        activeIdSet,
        inactiveIdSet,
        storeActiveUsers: this.$store.getters['tasks/getActiveUsersList'] || [],
      });
    },
    userOptions() {
      const selectedIds = [
        ...normalizeUserIdList(this.localValue.preparer),
        ...normalizeUserIdList(this.localValue.approver),
        ...normalizeUserIdList(this.localValue.sign_off_approvers),
      ];
      const activeIdSet = new Set();
      (this.activeCatalogUsers || []).forEach((u) => {
        const id = entityId(u);
        if (id) activeIdSet.add(id);
      });
      (this.$store.getters['tasks/getActiveUsersIds'] || []).forEach((id) => {
        if (id) activeIdSet.add(String(id));
      });
      return buildAssignmentUserOptions({
        activeUsers: this.activeCatalogUsers,
        allUsers: this.allUsers,
        selectedIds,
        isEdit: this.isEdit,
        activeIdSet,
      });
    },
    /**
     * Until usersforlocations resolves for the current locations, keep catalog
     * options (incl. edit-mode selected) so chips do not blank while loading.
     */
    locationScopedUsers() {
      if (!this.locationIdsKey) return [];
      if (this.lastFetchedLocationIdsKey !== this.locationIdsKey) {
        return this.userOptions;
      }
      return filterUsersToAllowedIds(this.userOptions, this.allowedUserIds);
    },
    preparerExcludeIds() {
      return excludeIdsForAssignmentRole(this.localValue, USER_ROLE.PREPARER);
    },
    approverExcludeIds() {
      return excludeIdsForAssignmentRole(this.localValue, USER_ROLE.APPROVER);
    },
    signOffExcludeIds() {
      return excludeIdsForAssignmentRole(this.localValue, USER_ROLE.SIGN_OFF_APPROVERS);
    },
    signOffApproverUsers() {
      const blocked = new Set(this.signOffExcludeIds);
      return this.locationScopedUsers.filter((user) => !blocked.has(String(user._id)));
    },
    /**
     * Stable key so usersforlocations refetches only when location membership
     * changes — not when preparer/approver updates rebuild localValue arrays.
     */
    locationIdsKey() {
      return [...(this.value?.location_ids || [])]
        .map(String)
        .filter(Boolean)
        .sort()
        .join(',');
    },
  },

  watch: {
    active: {
      handler(isActive) {
        if (isActive) this.refreshAllowedUsers();
      },
      immediate: true,
    },
    locationIdsKey() {
      if (this.active) this.refreshAllowedUsers();
    },
  },

  methods: {
    async refreshAllowedUsers() {
      if (!this.active) return;

      const locationIds = [...(this.localValue.location_ids || [])].map(String).filter(Boolean);
      const key = this.locationIdsKey;

      if (!locationIds.length) {
        this.usersFetchToken += 1;
        this.usersFetchLoading = false;
        this.allowedUserIds = [];
        this.lastFetchedLocationIdsKey = '';
        return;
      }

      // Dialog-session cache: same locations → skip refetch when revisiting Assignment.
      if (this.lastFetchedLocationIdsKey === key) return;

      const token = ++this.usersFetchToken;
      this.usersFetchLoading = true;
      try {
        const ids = await fetchUsersForLocations({ locationIds });
        if (token !== this.usersFetchToken) return;
        this.allowedUserIds = ids;
        this.lastFetchedLocationIdsKey = key;
        this.applyDropIdsNotAllowed();
      } catch (err) {
        if (token !== this.usersFetchToken) return;
        console.error('[AssignmentBlock]: usersForLocations error', err);
      } finally {
        if (token === this.usersFetchToken) this.usersFetchLoading = false;
      }
    },

    applyDropIdsNotAllowed() {
      const next = {
        ...this.localValue,
        preparer: dropIdsNotAllowed(this.localValue.preparer, this.allowedUserIds),
        approver: dropIdsNotAllowed(this.localValue.approver, this.allowedUserIds),
        sign_off_approvers: dropIdsNotAllowed(this.localValue.sign_off_approvers, this.allowedUserIds),
        assignment_number: this.assignmentNumber,
      };
      const changed =
        next.preparer.length !== (this.localValue.preparer || []).length
        || next.approver.length !== (this.localValue.approver || []).length
        || next.sign_off_approvers.length !== (this.localValue.sign_off_approvers || []).length
        || next.preparer.some((id, i) => String(id) !== String(this.localValue.preparer[i]))
        || next.approver.some((id, i) => String(id) !== String(this.localValue.approver[i]))
        || next.sign_off_approvers.some((id, i) => String(id) !== String(this.localValue.sign_off_approvers[i]));
      if (changed) this.emitAssignment(next);
    },

    updateAssignmentRole(field, rawValue) {
      const synced = syncAssignmentRolesOnChange(this.localValue, field, rawValue);
      this.emitAssignment({
        ...synced,
        assignment_number: this.assignmentNumber,
      });
    },

    onRequiresSignOffToggle() {
      const next = !this.localValue.requires_sign_off;
      this.emitAssignment({
        ...this.localValue,
        requires_sign_off: next,
        sign_off_approvers: next ? [...(this.localValue.sign_off_approvers || [])] : [],
        assignment_number: this.assignmentNumber,
      });
    },

    emitAssignment(next) {
      this.$emit('input', next);
    },

    updateField(key, val) {
      const next = {
        ...this.localValue,
        assignment_number: this.assignmentNumber,
        [key]: val,
      };

      if (
        key === 'location_ids'
        && assignmentHasMixedLocationTypes(val, this.locationOptions)
        && !this.hasMixedLocationTypes
      ) {
        this.$store.commit('sbError', ASSIGNMENT_LOCATION_TYPE.MIXED_ERROR);
      }

      // Drop assignment GLs that no longer match Accounts pool + selected locations.
      if (key === 'location_ids' || key === 'glcodes') {
        const allowedAccounts = filterAccountsForAssignment({
          chartOfAccounts: this.chartOfAccounts,
          availableGlCodes: this.availableGlCodes,
          locationIds: key === 'location_ids' ? val : next.location_ids,
          locations: this.locationOptions,
        });
        const allowed = new Set();
        allowedAccounts.forEach((c) => {
          [c?.glcode, c?._id, c?.id]
            .filter((v) => v != null && String(v).trim())
            .forEach((v) => allowed.add(String(v)));
        });
        next.glcodes = (next.glcodes || []).filter((g) => allowed.has(String(g)));
      }

      this.emitAssignment(next);
    },

    onSkipValidationsToggle() {
      this.updateField('enforceThreshold', !this.localValue.enforceThreshold);
    },

    onAutoApproveToggle() {
      this.updateField('auto_approve', !this.localValue.auto_approve);
    },
  },
};
</script>
